/**
 * How the widget shows an instant to the visitor.
 *
 * THE FAILURE THIS FILE PREVENTS, stated first because everything below is a
 * consequence of it: an agent in Kyiv writes "18:00" and a visitor in Lisbon
 * reads "18:00", and one of them turns up two hours late. A time without a zone
 * is not a time, it is a number that happens to be right for whoever produced it.
 * `Transcript.vue` renders one on every message, and every commitment this panel
 * will ever render — business hours, a callback slot, "we reply by" — is the same
 * hazard with a bigger consequence.
 *
 * SO THERE ARE TWO RULES, AND THE SECOND ONE IS THE WHOLE DESIGN.
 *
 * 1. A PAST instant, rendered in a zone we DETECTED, needs no qualifier. It is
 *    the reader's own clock; "09:05" means their 09:05 and saying "09:05 GMT+3"
 *    to a person sitting in GMT+3 is noise, and noise is what teaches people to
 *    stop reading the part that matters.
 * 2. Everything else carries the offset. That is: any instant rendered in the
 *    FALLBACK zone (see timezone.ts — we did not detect the visitor's, so this is
 *    explicitly not their clock), and every FUTURE commitment regardless of
 *    detection, because a commitment is acted on by two parties who are not in
 *    the same place and the reader cannot tell from "18:00" whose 18:00 it is.
 *
 * WHY `shortOffset` ("GMT+3") AND NOT A ZONE NAME. `Intl` will also render
 * `short` ("EEST" — an abbreviation few visitors decode, and locale-unstable: the
 * same zone is "GMT+3" in Ukrainian and "EEST" in English) or `shortGeneric`
 * ("час: Україна", "New York Time"). The last one is disqualified on privacy
 * grounds rather than taste: it prints a PLACE back at the visitor, on a
 * customer's page, turning a rendering detail into a visible statement about
 * where they are. An offset answers "which 18:00" and says nothing about anyone.
 *
 * This does NOT contradict `packages/i18n/src/time-zone.ts`, which refuses
 * offsets, and the distinction matters: that file refuses to COMPUTE with an
 * offset, because "+02:00" is a fact about one day in Kyiv rather than about
 * Kyiv, and a stored offset is an hour wrong for five months a year. Here the
 * arithmetic has already been done by a named zone that knows its own DST rules,
 * and the offset is only the LABEL on the answer — which is why it reads GMT+2 in
 * January and GMT+3 in September for the same zone. `format.test.ts` asserts both.
 *
 * WHY THIS IS NOT IN `@axon/i18n`. It is presentation, and it is the widget's:
 * the console shows an agent one business zone and has no fallback to disclose.
 * The `Formatters` from the package do all the actual formatting — this file adds
 * only the qualifier and the transcript's "today or not" rule, and the moment a
 * second app wants either, they belong upstream.
 *
 * WHAT BREAKS IF THIS CHANGES. Drop `zoneKnown` and a time rendered in the
 * default zone becomes indistinguishable from the visitor's own — which is
 * exactly the missed appointment, arriving silently. Swap the qualifier for
 * `shortGeneric` and the widget starts naming the visitor's region back at them.
 * Hand-roll the time (`d.getHours()`) and a Ukrainian visitor reads "9:5".
 *
 * NO GLOBAL CACHE, deliberately. `Intl` construction is roughly two orders of
 * magnitude dearer than formatting, and the package's own formatters are cached
 * for that reason. The two built here are built ONCE PER FACTORY CALL, and the
 * factory is meant to be memoised on `(locale, zone)` by its caller the way
 * `OmniI18nProvider` memoises `createFormatters`. The cost of that choice, named:
 * a caller that invokes this per render pays two `Intl.DateTimeFormat`
 * constructions per render, and nothing in this file will tell them.
 */
import { createFormattersForTag, type DateInput, type TimeZone } from '@axon/i18n';
import { WIDGET_LOCALE_BCP47, type WidgetLocale } from './locales';

import type { VisitorTimeZone } from './timezone';

export interface VisitorFormat {
  readonly locale: WidgetLocale;
  /** The zone every method below renders in. */
  readonly timeZone: TimeZone;
  /**
   * Whether {@link VisitorFormat.timeZone} is the visitor's own. False means it
   * is the stated default and every rendered time carries its offset — see rule 2
   * in this file's header.
   */
  readonly zoneKnown: boolean;

  /** Wall-clock time. Qualified when the zone is not the visitor's own. */
  time(value: DateInput): string;
  /** Date and wall-clock time. Qualified when the zone is not the visitor's own. */
  dateTime(value: DateInput): string;
  /**
   * The transcript's rule: a message from today is a time, anything older is a
   * date and a time. "Today" is the CALENDAR day in the rendering zone, not
   * "within 24 hours" — a reply at 23:50 read at 00:10 is yesterday's, and saying
   * so is the difference between a visitor thinking they were answered tonight
   * and knowing they were answered last night.
   */
  timestamp(value: DateInput, now?: DateInput): string;
  /**
   * A commitment: a callback, an opening time, anything the visitor is expected
   * to turn up for. ALWAYS carries the offset, even in a zone we detected,
   * because the other party to it is somewhere else.
   */
  appointment(value: DateInput): string;
  /** The offset in force at `at` — "GMT+3". DST-dependent, hence the argument. */
  zoneLabel(at?: DateInput): string;
}

/**
 * Mirrors `toDate` in `packages/i18n/src/format.ts`, which is not exported.
 *
 * Six duplicated lines against the alternative of rendering the literal string
 * "Invalid Date" into a support transcript, where it would be read as our bug by
 * the one person least able to report it. If a third caller needs this, the
 * package should export it rather than grow a third copy.
 */
function toInstant(value: DateInput): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`not a valid date: ${JSON.stringify(value)}`);
  }
  return date;
}

export interface VisitorFormatContext {
  readonly locale: WidgetLocale;
  readonly zone: VisitorTimeZone;
}

export function createVisitorFormat(context: VisitorFormatContext): VisitorFormat {
  const { locale } = context;
  const { timeZone, source } = context.zone;
  const zoneKnown = source === 'device';
  const tag = WIDGET_LOCALE_BCP47[locale];

  // Every unqualified rendering comes from the shared formatters, so the widget
  // cannot drift into a second convention for what a date looks like.
  const base = createFormattersForTag(locale, tag, timeZone);

  // `timeStyle`/`dateStyle` and `timeZoneName` are mutually exclusive — `Intl`
  // throws "Invalid option" if you pass both — so the qualified shapes have to
  // name their components. The components chosen are the ones that reproduce
  // `dateStyle: 'medium'` / `timeStyle: 'short'` exactly, and `format.test.ts`
  // asserts that for every supported locale, so a locale whose short time is
  // `9:05 AM` cannot silently start rendering `09:05 AM` next to it.
  const qualifiedTime = new Intl.DateTimeFormat(tag, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'shortOffset',
  });

  const qualifiedDateTime = new Intl.DateTimeFormat(tag, {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'shortOffset',
  });

  const renderTime = (value: DateInput): string =>
    zoneKnown ? base.time(value) : qualifiedTime.format(toInstant(value));

  const renderDateTime = (value: DateInput): string =>
    zoneKnown ? base.dateTime(value) : qualifiedDateTime.format(toInstant(value));

  const zoneLabel = (at: DateInput = Date.now()): string => {
    const parts = qualifiedTime.formatToParts(toInstant(at));
    const named = parts.find((part) => part.type === 'timeZoneName');
    // Unreachable with the options above; not asserted away, because the remedy
    // for a runtime that omits the part is to say nothing rather than to throw
    // inside a transcript render.
    return named?.value ?? '';
  };

  return {
    locale,
    timeZone,
    zoneKnown,

    time: renderTime,

    dateTime: renderDateTime,

    timestamp: (value, now = Date.now()) => {
      // Compared through `isoDate`, which is the package's own "what calendar day
      // is this instant in, in this zone" — the same function `relativeTime` uses
      // for "yesterday". Comparing epoch milliseconds instead would be wrong by a
      // day at every local midnight and by an hour twice a year.
      const sameDay = base.isoDate(value) === base.isoDate(now);
      return sameDay ? renderTime(value) : renderDateTime(value);
    },

    appointment: (value) => qualifiedDateTime.format(toInstant(value)),

    zoneLabel,
  };
}
